/**
 * Feature flags configuration.
 *
 * Flags are read from the runtime config (served by GET /config from server.ts),
 * which in turn reads FEATURE_* environment variables on the container.
 *
 * To enable a feature, set the corresponding env var on the frontend server:
 *   FEATURE_BRAINSTORM=true
 *   FEATURE_AUTOPILOT=true
 *   FEATURE_CANVAS=true
 *   FEATURE_WORKFLOWS=true
 *   FEATURE_APPS=true
 *   FEATURE_TEAMS_INTEGRATION=true
 *   FEATURE_OUTLOOK_INTEGRATION=true
 *
 * If not set or set to any value other than "true", "1", or "yes", the
 * feature defaults to disabled (false).
 *
 * For local development, add these to `frontend/.env`.
 */

import { getRuntimeConfig, isRuntimeConfigReady } from "./runtime";

/** Static defaults — used as fallback before runtime config is loaded. */
const defaults = {
  brainstorm: false,
  autopilot: false,
  canvas: false,
  workflows: false,
  /** Custom apps page and team hub app sections */
  apps: false,
  /** Micro Apps — publish/share/discover generated dashboards as first-class apps */
  microApps: false,
  /** Multi-brand — choose which brand generated output uses (picker + select_brand tool) */
  multiBrand: false,
  /** Auto agent routing — the picker's "Auto" sends a new chat to the best agent (needs the backend flag too) */
  agentRouting: false,
  /** Agent handoff — an agent can hand the chat to a better-suited one mid-conversation (needs the backend flag too) */
  agentHandoff: false,
  /** Microsoft Teams integration (requests Teams Graph scopes at login) */
  teamsIntegration: false,
  /** Outlook / Exchange mail integration (requests Mail.Read Graph scope at login) */
  outlookIntegration: false,
  /** Developer mode — shows context usage details in chat */
  devMode: false,
  /** Import skills/agents from Agent-Skills bundles (My Skills page + import) */
  skillImport: false,
  /** RALLY — Remote Access Link & Loopback Yoke: remote harness connector (Settings tab) */
  rally: false,
} as const;

export type FeatureFlag = keyof typeof defaults;

/**
 * Live feature flags object. Reads from runtime config if available,
 * otherwise returns static defaults (all false).
 */
export const featureFlags: Record<FeatureFlag, boolean> = new Proxy(defaults as Record<FeatureFlag, boolean>, {
  get(_target, prop: string) {
    if (prop in defaults) {
      if (isRuntimeConfigReady()) {
        return getRuntimeConfig().featureFlags[prop as FeatureFlag] ?? false;
      }
      return false;
    }
    return undefined;
  },
});
